import { adaptFileUrl } from '@unifyapps/network/hooks/useAdaptFileUrl/useAdaptFileUrl';
import { parseCookie } from '@unifyapps/network/utils/cookie';
import type { QueryMeta } from '@unifyapps/network/utils/fetch/types';
import { getCookie } from '@unifyapps/network/utils/native/localStorage';
import compact from 'lodash/compact';
import omit from 'lodash/omit';
import { adaptSearchToFilter } from '../../../hooks/useAggregationQueryBuilder';
import { formatDate, formatDateByFormatType } from '../../../utils/date';
import { downloadFileInSameTab } from '../../../utils/file/downloadFileInSameTab';
import { safeJsonParse, safeJsonStringify } from '../../../utils/json';
import { formatNumber } from '../../../utils/number';
import { formatTime } from '../../../utils/time';

export const ComputeContextKeys = {
  PAGE_VARIABLES: '__PAGE_VARS__',
  PAGE_FUNCTIONS: '__PAGE_FUNCTIONS__',
  DEPRECATED_SESSION_STORAGE: '__SESSION_STORAGE__',
  SESSION_STORAGE: 'sessionStorage',
  LOCAL_STORAGE: 'localStorage',
  LOCATION: 'location',
  UTILS: 'utils',
  PAGE_INPUTS: 'pageInputs',
  PAGE_PARAMS: 'pageParams',
  USER_CONTEXT: 'userContext',
  DEVICE: 'deviceContext',
  INTERFACE_RECORD: 'interfaceRecord',
  RUNTIME_BLOCKS: 'runtimeBlocks',
  DATA_SOURCES_META: 'dataSourcesMeta',
  WALKTHROUGH: 'walkthrough',
  ENV_VARIABLES: 'envVariables',
  QUERY_META: 'queryMeta',
  MEDIA_UPLOADS: 'mediaUploads',
  THEME: 'theme',
  ACTIONS: 'actions',
  APPS_AND_INTEGRATIONS: 'appsAndIntegrations',
  SERVER_UTILS: 'serverUtils',
};

/**
 * When creating dependency graph, we have more entityId {{ <entityId>.something }} that we reference in blocks, but
 * these are added to context at runtime and not stored in the block state.
 */
export const RESERVED_COMPUTE_CONTEXT_KEYS = Object.values(omit(ComputeContextKeys, 'UTILS'));
export const BLOCKS_STATE_WITH_METHOD = 'blocksStateWithMethod';

// Top-level compute-context keys hidden from the expression editor's
// autocomplete: deprecated namespace aliases (the real page vars/functions are
// spread at root by name) and internal ref maps that are never authored by hand.
export const HIDDEN_AUTOCOMPLETE_SCOPE_KEYS: ReadonlySet<string> = new Set([
  ComputeContextKeys.PAGE_VARIABLES, // '__PAGE_VARS__' — deprecated alias
  ComputeContextKeys.PAGE_FUNCTIONS, // '__PAGE_FUNCTIONS__' — deprecated alias
  ComputeContextKeys.DEPRECATED_SESSION_STORAGE, // '__SESSION_STORAGE__'
  'blockRefs', // internal RefObject map
]);

export const getTodayStartTimestamp = () => {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return startOfToday.getTime();
};

/**
 * Defines the mode of operation for the compute context.
 *
 * - 'readonly': Does not populate methods for blocks, page variables, and page functions,
 *   preventing mutation of the global state store. This is the default and preferred mode
 *   for most use cases as it improves performance by avoiding unnecessary function creation.
 *
 * - 'mutable': Creates methods for all entities (blocks, page variables, page functions),
 *   allowing them to mutate the global state store. This mode should only be used when
 *   state mutations are explicitly required.
 *
 * Using 'readonly' mode provides significant performance benefits by reducing function
 * creation and reference management overhead when creating compute contexts.
 */
export type ComputeContextMode = 'readonly' | 'mutable';

const getDownloadFileUtilFn = (queryMeta?: QueryMeta) => {
  return (blob: string | Blob, filename: string) => {
    const adaptedUrl =
      typeof blob === 'string' ? adaptFileUrl(blob, queryMeta, { preview: false }) : blob;
    downloadFileInSameTab(adaptedUrl, filename);
  };
};

/*
 * WARNING: These utilities are exposed in the no-code platform.
 * Any new utility additions must be:
 * 1. Documented in packages/carbon/docs/website/utilities.md
 * 2. Backward compatible with existing implementations
 * 3. Added with appropriate warning comments in their source files
 * Do not add new utilities without updating the documentation first.
 */
export const getContextUtils = (queryMeta?: QueryMeta) => ({
  formatDate,
  formatDateByFormatType,
  formatTime,
  downloadFile: getDownloadFileUtilFn(queryMeta),
  parseJson: safeJsonParse,
  stringifyJson: safeJsonStringify,
  formatNumber,
  dateTime: {
    values: {
      todayDate: formatDate(new Date(), 'dd-MM-yyyy'),
      startOfTodayTimestamp: getTodayStartTimestamp(),
    },
  },
  // Internal fncs
  adaptSearchToFilter,
  compact,
});

export const getMobileContextUtils = (queryMeta?: QueryMeta) => ({
  ...getContextUtils(queryMeta),
  getAuthToken: () => {
    const cookie = getCookie();
    const { authToken: token } = parseCookie(cookie ?? '');
    return token ?? '';
  },
});
